import { NPCS } from '../../content/npcs';
import { dist } from '../../core/math';
import type { Game } from '../../game/Game';
import type { ZoneRuntime } from '../../game/ZoneRuntime';
import type { Actor } from '../../sim/actor';
import type { EntityId } from '../../sim/types';
import { api } from '../../ui/app/api';
import { bump } from '../../ui/app/store';
import { CLIP_SECONDS, pickClip } from '../lifeskills/anim';
import { EMOTE_BOND_RANGE, EMOTE_BY_ID } from './data';
import { emoteFor } from './logic';

/**
 * Emote playback on actor views. `playEmote(zone, actorId, emoteId)` works for
 * any actor (the local player or a replicated remote player); looping emotes
 * (dance, sit) are kept alive by `emoteSystem` until the actor moves, casts
 * or dies.
 */

interface ActiveEmote {
  zone: ZoneRuntime;
  clip: string;
  loop: boolean;
  /** Clip cycle length (s). */
  period: number;
  /** Seconds until the next re-trigger (looping clips). */
  t: number;
  elapsed: number;
  /** Stop after this many seconds (null = until the actor moves). */
  until: number | null;
  x: number;
  z: number;
}

const active = new Map<EntityId, ActiveEmote>();

/** Clips available on an actor's model (null when unknown). */
export function actorClips(zone: ZoneRuntime, actorId: EntityId): Set<string> | null {
  const a = zone.world.actors.get(actorId);
  if (!a) return null;
  const lib = zone.opts.assets;
  const id = lib.resolve(a.look.model);
  const clips = id ? lib.entry(id)?.clips : undefined;
  return clips ? new Set(clips) : null;
}

function start(zone: ZoneRuntime, actorId: EntityId, chain: readonly string[], loop: boolean, seconds: number | null): boolean {
  const a = zone.world.actors.get(actorId);
  const v = zone.views.get(actorId);
  if (!a || !v || !a.alive) return false;
  const clip = pickClip(chain, actorClips(zone, actorId));
  if (!clip) return false;
  const period = CLIP_SECONDS[clip] ?? 1.5;
  if (loop) v.play(clip, 0.12, period, false);
  else v.play(clip, 0.12);
  active.set(actorId, { zone, clip, loop, period, t: period - 1 / 30, elapsed: 0, until: loop ? seconds : period, x: a.pos.x, z: a.pos.z });
  return true;
}

/**
 * Plays emote `emoteId` (wave, cheer, bow, dance, sit, laugh, point, salute)
 * on actor `actorId`'s view in `zone`. Call it for remote players when their
 * emote is replicated. Returns false for an unknown emote/actor or a view that
 * hasn't loaded yet.
 */
export function playEmote(zone: ZoneRuntime, actorId: EntityId, emoteId: string): boolean {
  const def = EMOTE_BY_ID[emoteId];
  if (!def) return false;
  return start(zone, actorId, def.clips, def.loop, null);
}

/** Stops an actor's emote; `release` returns the view to Idle/Run immediately. */
export function stopEmote(zone: ZoneRuntime, actorId: EntityId, release = true): void {
  const e = active.get(actorId);
  if (!e || e.zone !== zone) return;
  active.delete(actorId);
  const a = zone.world.actors.get(actorId);
  if (release && !a?.cast) zone.views.get(actorId)?.endStage();
}

/** Keeps looping emotes alive and ends emotes of actors that moved. */
export function emoteSystem(game: Game, dt: number): void {
  for (const [id, e] of active) {
    const z = e.zone;
    const a = z === game.zone ? z.world.actors.get(id) : undefined;
    const v = z.views.get(id);
    if (!a || !v || !a.alive) {
      active.delete(id);
      continue;
    }
    if (a.cast || dist(a.pos, e) > 0.25) {
      active.delete(id);
      if (!a.cast) v.endStage();
      continue;
    }
    e.elapsed += dt;
    if (e.until !== null && e.elapsed >= e.until) {
      active.delete(id);
      continue;
    }
    if (!e.loop) continue;
    e.t -= dt;
    if (e.t <= 0) {
      v.play(e.clip, 0.1, e.period, false);
      e.t = e.period - 1 / 30;
    }
  }
}

/** Nearest bond NPC to an actor within `range` (m). */
export function nearestBondNpc(game: Game, from: Actor, range: number): Actor | null {
  const z = game.zone;
  if (!z) return null;
  let best: Actor | null = null;
  let bestD = range;
  for (const a of z.world.actors.values()) {
    if (a.kind !== 'npc' || !a.npcId || !NPCS[a.npcId]?.bond) continue;
    const d = dist(a.pos, from.pos);
    if (d <= bestD) {
      best = a;
      bestD = d;
    }
  }
  return best;
}

/**
 * The local player emotes: stops, plays the clip, tells the game/network
 * (`api().emote`) and — when a bond NPC stands close by and emotes are left
 * today — counts toward that NPC's bond.
 */
export function performPlayerEmote(game: Game, emoteId: string, opts: { bond?: boolean } = {}): boolean {
  const z = game.zone;
  const p = game.player;
  const prog = game.progression;
  if (!z || !p || !p.alive || !EMOTE_BY_ID[emoteId]) return false;
  z.world.stop(p);
  const npc = opts.bond === false ? null : nearestBondNpc(game, p, EMOTE_BOND_RANGE);
  if (npc) z.world.faceTowards(p, npc.pos);
  playEmote(z, p.id, emoteId);
  api().emote(emoteId);
  if (npc?.npcId && prog) {
    const r = emoteFor(prog, game.account.day, npc.npcId, emoteId);
    if (r.ok) {
      game.hud.toast(`${npc.name}: ${r.line} <span style="color:#ff90c0">Bond +${r.points}</span>`, '#ff90c0');
      game.store.saveSoon(game.account);
      bump();
    }
  }
  return true;
}

/** Plays a short "performing a song" loop on the local player, facing the NPC. */
export function performSong(game: Game, npc: Actor | null): void {
  const z = game.zone;
  const p = game.player;
  if (!z || !p || !p.alive) return;
  z.world.stop(p);
  if (npc) z.world.faceTowards(p, npc.pos);
  start(z, p.id, ['Channel', 'Cast', 'Buff'], true, 2.6);
}
